import React from 'react'
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { COMMUNICATION_STATUS, COMMUNICATION_TYPE } from "data/static/staticOptions";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";

import { useAuth } from "hooks";


const INITIAL_STATE = {
  account: "",
  entity: "",
  shipmentId: "",
  senderSystem: "",
  receiverSystem: "",
  communicationType: "",
  message: "",
  communicationTime: "",
  status: "",
  isActive: ""
}
const CommunicationForm = ({
  isBusy = null,
  communicationDetail = null,
  onSubmit = () => { },
}) => {

  const navigate = useNavigate();

  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // react hook form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,

  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  // RTK Query
  const
    {
      data: accounts,
      isLoading: loadingAccounts,
    }
      = useGetDDLAccountListQuery();

  // RTK Query
  const [
    fetchEntities,
    {
      data: entities,
      isLoading: loadingEntities,
    }]
    = useLazyGetDDLEntityListQuery();
  const [
    fetchShipments,
    { data: shipments, isLoading: loadingShipments }
  ] = useLazyGetDDLShipmentListQuery();

  // methods
  const handleOnCommunicationSubmit = (data) => {
    const _communicationPayload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      shipmentId: data?.shipmentId?._id,
    }
    onSubmit(_communicationPayload);
  };

  React.useEffect(() => {
    if (!communicationDetail) return;
    reset({
      ...communicationDetail,
      communicationTime: communicationDetail?.communicationTime
        ? new Date(communicationDetail.communicationTime).toISOString().slice(0, 16)
        : "",
    })
  }, [communicationDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({ account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id });
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin, viewEntity?.entity?.account]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return
    fetchShipments({ account: _watchAccount?._id, entity: _watchEntity?._id })
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipments, viewEntity])

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "")

                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicleId", "")
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Shipment ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);

                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) => option?._id === value?._id}
                  options={shipments || []}
                  loading={loadingShipments}
                  loadingText="Loading ShipmentFD..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="senderSystem"
              control={control}
              rules={{ required: "senderSystem is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Sender System"
                  error={!!errors.senderSystem}
                  helperText={errors?.senderSystem?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="receiverSystem"
              control={control}
              rules={{ required: "receiverSystem is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Receiver System"
                  error={!!errors.receiverSystem}
                  helperText={errors?.receiverSystem?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="communicationType"
              control={control}
              rules={{ required: "Add communication Type" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Communication Type"
                  options={COMMUNICATION_TYPE}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="message"
              control={control}
              rules={{ required: "message is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Message"
                  error={!!errors.message}
                  helperText={errors?.message?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="communicationTime"
              control={control}
              rules={{ required: "Communication Time is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Transfer Time"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true, }}
                  error={!!errors.communicationTime}
                  helperText={errors?.communicationTime?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Add status" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Status"
                  options={COMMUNICATION_STATUS}
                />
              )}
            />
          </Grid>

          {communicationDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained"
          onClick={handleSubmit(handleOnCommunicationSubmit)}
        >
          {communicationDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  )
}

export default CommunicationForm